import { useCallback, useEffect, useState } from 'react';

export type PlanningAiAvailability = 'unknown' | 'available' | 'unavailable';

const STORAGE_KEY = 'cityscope:planning-ai-availability';
const EVENT_NAME = 'cityscope:planning-ai-availability-changed';
const AVAILABLE_TTL_MS = 30 * 60 * 1000;
const UNAVAILABLE_TTL_MS = 10 * 60 * 1000;

type StoredAvailability = {
  status: Exclude<PlanningAiAvailability, 'unknown'>;
  expiresAt: number;
};

const readStoredAvailability = (): PlanningAiAvailability => {
  if (typeof window === 'undefined') return 'unknown';
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return 'unknown';
    const stored = JSON.parse(raw) as Partial<StoredAvailability>;
    if ((stored.status !== 'available' && stored.status !== 'unavailable')
      || !Number.isFinite(stored.expiresAt)
      || Number(stored.expiresAt) <= Date.now()) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return 'unknown';
    }
    return stored.status;
  } catch {
    return 'unknown';
  }
};

const writeAvailability = (status: Exclude<PlanningAiAvailability, 'unknown'>): void => {
  if (typeof window === 'undefined') return;
  const ttl = status === 'available' ? AVAILABLE_TTL_MS : UNAVAILABLE_TTL_MS;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ status, expiresAt: Date.now() + ttl }));
  } catch {
    // The in-page event still updates sibling controls when storage is blocked.
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { status } }));
};

export const usePlanningAiAvailability = () => {
  const [status, setStatus] = useState<PlanningAiAvailability>('unknown');

  useEffect(() => {
    setStatus(readStoredAvailability());
    const handleChange = (event: Event) => {
      const nextStatus = (event as CustomEvent<{ status?: PlanningAiAvailability }>).detail?.status;
      setStatus(nextStatus === 'available' || nextStatus === 'unavailable' ? nextStatus : readStoredAvailability());
    };
    window.addEventListener(EVENT_NAME, handleChange);
    return () => window.removeEventListener(EVENT_NAME, handleChange);
  }, []);

  const markAvailable = useCallback(() => {
    setStatus('available');
    writeAvailability('available');
  }, []);

  const markUnavailable = useCallback(() => {
    setStatus('unavailable');
    writeAvailability('unavailable');
  }, []);

  return { status, markAvailable, markUnavailable };
};
